///
/// EarthBrowser
///
/// Created by Matt Giger on Dec 20, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { type Database } from "@/earthbrowser/database/Database"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineBridge } from "@/earthbrowser/engineBridge"
import { DatasetLayer, type DatasetManifest, type DatasetSceneObject, type DatasetSyncConfig } from "../DatasetLayer"
import { VolcanoesTable, type VolcanoProperties } from "./VolcanoesTable"
import { VolcanoFeature } from "./VolcanoFeature"

/** Raw Arrow row shape for the volcanoes feed (only fields we read) */
interface VolcanoRow {
	id: unknown
	number: unknown
	name: unknown
	type: unknown
	country: unknown
	region: unknown
	subregion: unknown
	lon: unknown
	lat: unknown
	elevation_m: unknown
	last_eruption: string | null | undefined
	epoch: string | null | undefined
	landform: string | null | undefined
	tectonic_setting: string | null | undefined
	evidence_category: string | null | undefined
	rock_type: string | null | undefined
	geological_summary: string | null | undefined
	photo_link: string | null | undefined
	photo_caption: string | null | undefined
	photo_credit: string | null | undefined
	updated_at: string | null | undefined
}

export class VolcanoesDatasetLayer extends DatasetLayer<VolcanoProperties> {
	private unsubscribeRedux?: () => void

	constructor(context: EarthBrowserContext) {
		super(context, 'volcanoes')
		this.subscribeToVisibility()
	}

	private subscribeToVisibility(): void {
		this.visible = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("volcano") ?? false
		this.unsubscribeRedux = engineBridge.subscribe(() => {
			const isVisible = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("volcano") ?? false
			if (isVisible !== this.visible) {
				this.visible = isVisible
			}
		})
	}

	override destroy(): void {
		this.unsubscribeRedux?.()
		super.destroy()
	}

	// ==================== Sync Configuration ====================

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "volcanoes",
			pollIntervalMs: 0, // No polling - volcanoes don't update frequently
			needsSync: true,
			supportsDelta: false,
		}
	}

	public async createTable(db: Database): Promise<void> {
		await VolcanoesTable.create(db)
	}

	public override async getStoredRowCount(_datasetId: string): Promise<number | null> {
		return VolcanoesTable.getCount()
	}

	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return

		const url = this.resolveUrl(shard.url, shard.path)
		console.log(`[VolcanoesDatasetLayer] Downloading shard: ${url}`)
		const rows = await downloadArrowRows(url)
		console.log(`[VolcanoesDatasetLayer] Parsed ${rows.length} rows`)

		const volcanoes = this.parseVolcanoRows(rows)
		await VolcanoesTable.insert(volcanoes)
	}

	private parseVolcanoRows(rows: unknown[]): VolcanoProperties[] {
		return (rows as VolcanoRow[]).map(r => ({
			id: String(r.id),
			number: Number(r.number),
			name: String(r.name),
			type: String(r.type),
			country: String(r.country),
			region: String(r.region),
			subregion: String(r.subregion),
			lon: Number(r.lon),
			lat: Number(r.lat),
			elevation_m: r.elevation_m === null || r.elevation_m === undefined ? null : Number(r.elevation_m),
			last_eruption: r.last_eruption ? String(r.last_eruption) : null,
			epoch: r.epoch ? String(r.epoch) : null,
			landform: r.landform ? String(r.landform) : null,
			tectonic_setting: r.tectonic_setting ? String(r.tectonic_setting) : null,
			evidence_category: r.evidence_category ? String(r.evidence_category) : null,
			rock_type: r.rock_type ? String(r.rock_type) : null,
			geological_summary: r.geological_summary ? String(r.geological_summary) : null,
			photo_link: r.photo_link ? String(r.photo_link) : null,
			photo_caption: r.photo_caption ? String(r.photo_caption) : null,
			photo_credit: r.photo_credit ? String(r.photo_credit) : null,
			updated_at: r.updated_at ? String(r.updated_at) : null,
		}))
	}

	// ==================== Scene Objects ====================

	public async queryFeatures(db: Database): Promise<VolcanoProperties[]> {
		const rows = await db.exec(`
			select
				id,
				number,
				name,
				type,
				country,
				region,
				subregion,
				lon,
				lat,
				elevation_m,
				last_eruption,
				epoch,
				landform,
				tectonic_setting,
				evidence_category,
				rock_type,
				geological_summary,
				photo_link,
				photo_caption,
				photo_credit,
				updated_at
			from
				volcanoes
			order by number asc
		`) as VolcanoProperties[]
		return rows
	}

	public createSceneObject(feature: VolcanoProperties): DatasetSceneObject<VolcanoProperties> {
		return new VolcanoFeature(this.context, feature)
	}
}
